/**
 * Highlights characters in a string that match any word in a search term, grouping
 * consecutive matching terms that appear in the original order.
 *
 * @param {string} text - The text to search within
 * @param {string} searchTerm - The search term to find and highlight (words separated by spaces)
 * @returns {Array<any>} Array of text and highlight objects
 */
export function highlightFuzzyMatchesWithConsecutiveTerms(
  text: string,
  searchTerm: string
): Array<TextHighlightMatch> {
  // If no search term was provided, return the original text.
  if (!searchTerm) {
    return [{ type: 'text', value: text }];
  }

  // Split the search term into individual words
  const searchWords: Array<string> = searchTerm
    .toLowerCase()
    .split(' ')
    .filter((word: string): boolean => word.length > 0);

  if (searchWords.length === 0) {
    return [{ type: 'text', value: text }];
  }

  // Lower case the text for case insensitive matching
  const lowerText: string = text.toLowerCase();
  const originalText: string = text;

  // Find all occurrences of each search word with their positions
  const wordMatches: Array<{ word: string; positions: Array<number> }> = searchWords
    .map((word: string): { word: string; positions: Array<number> } => ({
      word,
      positions: findAllPositionsOfWord(lowerText, word)
    }))
    .filter((match: { word: string; positions: Array<number> }): boolean => match.positions.length > 0);

  // If no matches were found, return the original text
  if (wordMatches.length === 0) {
    return [{ type: 'text', value: text }];
  }

  // Create ranges for individual words and check for consecutive terms
  const allRanges: Array<{ start: number; end: number }> = new Array<{ start: number; end: number }>();

  // First, add all individual word matches
  for (const match of wordMatches) {
    for (const position of match.positions) {
      allRanges.push({
        start: position,
        end: position + match.word.length
      });
    }
  }

  // Then find consecutive term matches
  for (let i: number = 0; i < searchWords.length - 1; i++) {
    const currentWord: string = searchWords[i];
    const nextWord: string = searchWords[i + 1];

    // Skip if either word doesn't have matches
    const currentMatches: { word: string; positions: Array<number> } = wordMatches.find(
      (match: { word: string; positions: Array<number> }): boolean => match.word === currentWord
    );
    const nextMatches: { word: string; positions: Array<number> } = wordMatches.find(
      (match: { word: string; positions: Array<number> }): boolean => match.word === nextWord
    );

    if (!currentMatches || !nextMatches) {
      continue;
    }

    // Check each position of the current word
    for (const currentPos of currentMatches.positions) {
      const currentEnd: number = currentPos + currentWord.length;

      // Find the position of the next word after the current word
      // Need to account for possible space or punctuation between words
      const textAfterCurrent: string = lowerText.substring(currentEnd);
      let offset: number = 0;

      // Skip any whitespace or punctuation
      while (offset < textAfterCurrent.length && /[\s.,;:!?]/.test(textAfterCurrent[offset])) {
        offset++;
      }

      // Check if the next position starts with the next word
      if (textAfterCurrent.substring(offset).startsWith(nextWord)) {
        const nextWordStart: number = currentEnd + offset;
        const nextWordEnd: number = nextWordStart + nextWord.length;

        // Add a range that covers both consecutive words including any space between
        allRanges.push({
          start: currentPos,
          end: nextWordEnd
        });
      }
    }
  }

  // Sort ranges by start position
  allRanges.sort(
    (a: { start: number; end: number }, b: { start: number; end: number }): number => a.start - b.start
  );

  // Merge overlapping ranges
  const mergedRanges: Array<{ start: number; end: number }> = new Array<{ start: number; end: number }>();
  if (allRanges.length > 0) {
    let current: { start: number; end: number } = allRanges[0];

    for (let i: number = 1; i < allRanges.length; i++) {
      if (allRanges[i].start <= current.end) {
        // Overlapping ranges, merge them
        current.end = Math.max(current.end, allRanges[i].end);
      } else {
        // Non-overlapping, add current to results and start a new current
        mergedRanges.push(current);
        current = allRanges[i];
      }
    }

    // Add the last range
    mergedRanges.push(current);
  }

  // Build result array with highlights
  const result: Array<TextHighlightMatch> = new Array<TextHighlightMatch>();
  let lastPosition: number = 0;

  for (const range of mergedRanges) {
    // Add text before highlight
    if (range.start > lastPosition) {
      result.push({
        type: 'text',
        value: originalText.substring(lastPosition, range.start)
      });
    }

    // Add highlight
    result.push({
      type: 'highlight',
      value: originalText.substring(range.start, range.end)
    });

    lastPosition = range.end;
  }

  // Add remaining text
  if (lastPosition < originalText.length) {
    result.push({
      type: 'text',
      value: originalText.substring(lastPosition)
    });
  }

  return result;
}

/**
 * Find all positions where a word appears in text
 *
 * @param {string} text - The text to search within (already lowercased)
 * @param {string} word - The word to find (already lowercased)
 * @returns {number[]} Array of starting positions
 */
function findAllPositionsOfWord(text: string, word: string): Array<number> {
  const positions: Array<number> = new Array<number>();
  let index: number = 0;

  while (index < text.length) {
    const position: number = text.indexOf(word, index);
    if (position === -1) {
      break;
    }

    positions.push(position);
    index = position + 1; // Move past the current match
  }

  return positions;
}

/** Represents a parsed section of text and how it should be displayed. */
export interface TextHighlightMatch {
  type: 'highlight' | 'text';
  value: string;
}
